import { createGlobalStyle } from 'styled-components';
import {COLORS, FONTS, FONT_SIZE, FONT_WEIGHT} from '../../../components/ui/theme';

/**
 * HeaderDAV2 — skin DA V2 du header + menu profil (icône), conforme à la maquette
 * validée `public/previews/dav2-profile-menu.html`.
 *
 * Rendu globalement par <Header> (donc sur toutes les pages marketplace). Cible les
 * classes EXISTANTES du header (`.de-menu-profile`, `.popshow`, `.de-submenu-profile`,
 * la cloche `.de-menu-notification`) → zéro réécriture de markup risquée.
 *
 * Valeurs littérales = tokens canoniques DA V2 (cf. maquette). À tokeniser vers theme.js
 * en suivi (§20) — littéraux ici pour éviter tout token `undefined` non catché par le build.
 */
export const HeaderDAV2 = createGlobalStyle`
  /* ============ FIX racine double-liste : solidnav → texte nav TOUJOURS sombre ============
     Cause des "menus invisibles" récurrents : une page dans la liste solidNav mais PAS dans
     la 2e liste reçoit aussi la classe legacy 'white' → '.navbar.white .btn{color:#fff}' rend
     le menu blanc sur fond blanc. On force ici le sombre dès que 'solidnav' est présent
     (spécificité id+3 classes + !important) → plus besoin de maintenir la 2e liste par page. */
  header#myHeader.navbar.solidnav .menu .btn,
  header#myHeader.navbar.solidnav .menu a,
  header#myHeader.navbar.solidnav .menu .navbar-item,
  header#myHeader.navbar.solidnav .menu .dropdown-custom.btn {
    color: #1a1430 !important;
  }

  /* ============ Menu nav FERRÉ À GAUCHE (gabarit preview dav2-fusion-B) ============
     Logo + menu collés à gauche ; le bloc droit (Connect / cloche+avatar) poussé à droite
     via margin-left:auto. Remplace l'ancien layout 3-colonnes qui CENTRAIT le menu. */
  /* Hauteur de barre FIXE et CONSTANTE sur toutes les pages — on supprime le padding
     variable qui faisait varier la hauteur ; seul le contenu droit change (loggué/délogué). */
  header#myHeader .row.w-100-nav {
    height: 4.25rem;
    min-height: 4.25rem;
    align-items: center;
  }
  /* Logo borne = gabarit preview (2.1rem). !important + width auto pour battre le legacy
     .logo img width:14vw (qui forcait ~49px de haut / 235px de large). */
  header#myHeader .logo img,
  header#myHeader .row.w-100-nav .logo img {
    height: 2.1rem !important;
    width: auto !important;
    max-width: none !important;
  }
  @media (min-width: 992px) {
    header#myHeader .row.w-100-nav { display: flex; align-items: center; flex-wrap: nowrap; }
    /* logo + menu = largeur naturelle, collés à gauche */
    header#myHeader .row.w-100-nav > .logo { flex: 0 0 auto; min-width: 0; }
    header#myHeader .row.w-100-nav > .menu { flex: 0 0 auto; margin: 0 0 0 1rem; }
    /* bloc droit poussé tout à droite (spacer) */
    header#myHeader .row.w-100-nav > .mainside { flex: 0 0 auto; margin-left: auto; display: flex; align-items: center; }
  }

  /* ============ Items de menu : italic + séparateur "•" CENTRÉ (gabarit preview) ============
     --menu-item-gap = espace inter-items ; le "•" est posé pile au MILIEU de cet espace :
     right = -(gap/2) place le bord droit du pseudo au point milieu, translateX(50%) recentre
     le glyphe sur ce point → centrage exact quel que soit le gap (§décision 4). */
  @media (min-width: 992px) {
    header#myHeader .menu { --menu-item-gap: 1.7rem; display: flex; align-items: center; gap: var(--menu-item-gap); }
    header#myHeader .menu .navbar-item { position: relative; display: inline-flex; align-items: center; }
    /* point de séparation centré entre chaque item sauf le dernier */
    header#myHeader .menu .navbar-item:not(:last-child)::after {
      content: "\\2022";
      position: absolute;
      right: calc(var(--menu-item-gap) / -2);
      transform: translateX(50%);
      color: rgba(13, 10, 16, .3);
      font-size: .7rem;
      line-height: 1;
      pointer-events: none;
    }
  }
  /* libellés nav — Roboto 600 non-italic (§header-redesign 2026-06-18). */
  header#myHeader .menu .navbar-item .dropdown-custom.btn {
    font-family: ${FONTS.body};
    font-weight: ${FONT_WEIGHT.semibold};
    font-style: normal;
    font-size: .92rem;
    color: #1a1430 !important;
    /* Neutralise le legacy padding ASYMÉTRIQUE (style.scss: 0 20px 0 0) qui élargissait
       la boîte à droite → le séparateur "•" (centré sur la boîte) penchait vers l'item suivant.
       Box hug l'encre → séparateur réellement équidistant entre les 2 textes (vérifié : écart 0-1px). */
    padding: 0 !important;
    margin: 0 !important;
    transition: color .14s ease;
  }
  /* hover = accent magenta */
  header#myHeader .menu .navbar-item:hover .dropdown-custom.btn {
    color: #C218A6 !important;
  }
  /* item actif = couleur accent + underline gradient sous le label */
  header#myHeader .menu .navbar-item a.active .dropdown-custom.btn {
    color: #C218A6 !important;
    position: relative;
  }
  header#myHeader .menu .navbar-item a.active .dropdown-custom.btn::after {
    content: "";
    position: absolute;
    bottom: -0.22rem; left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg, #C218A6, rgba(194,24,166,.2));
    border-radius: 2px;
    pointer-events: none;
  }
  /* Maps = dropdown : SEUL le wrapper trigger <div ref> ne doit pas élargir la boîte (sinon
     décalage du "•"). EXCLURE .navigation-content (le panneau dropdown) : c'est aussi un >div
     direct, et le forcer en flex le rendait VISIBLE en permanence (menu bloqué ouvert). */
  header#myHeader .menu .navbar-item.navigation > div:not(.navigation-content),
  header#myHeader .menu .navbar-item.navigation > div:not(.navigation-content) > a {
    display: inline-flex !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  /* ============ Sous-menu "Maps" (dropdown nav legacy) → panneau DA flottant ============
     Markup legacy: .navbar-item.navigation > .navigation-content (App.css = panneau gris
     #f1f1f1, min-width 160px, items #20005e, mal positionné). On le restyle en dropdown DA
     propre (blanc, liséré accent, arrondi, ombre) sous l'item, items en typo menu DA. */
  @media (min-width: 992px) {
    header#myHeader .menu .navigation { position: relative; }
    /* BRIDGE invisible : comble le trou de .7rem entre "Maps" et le panneau pour que le
       survol ne se perde PAS en descendant la souris (cause racine de l'irritant "timer").
       → sous-menu joignable + survol instantané, zéro JS (§décision 2).
       Sur ::before car ::after porte déjà le séparateur "•" de l'item. */
    header#myHeader .menu .navigation::before {
      content: "";
      position: absolute;
      top: 100%;
      left: 0;
      width: 100%;
      height: .9rem;
      background: transparent;
      pointer-events: auto;
    }
    header#myHeader .menu .navigation-content {
      top: calc(100% + .7rem);
      left: 0;
      min-width: 13rem;
      background: #fff;
      border: 1px solid var(--ev-line, #ececec);
      border-top: .22rem solid #C218A6;
      border-radius: 1rem;
      box-shadow: 0 1.2rem 3rem rgba(33,12,46,.18);
      padding: .4rem;
      z-index: 200;
    }
    header#myHeader .menu .navigation-content::before {
      content: "";
      position: absolute;
      top: -.5rem;
      left: 1.3rem;
      width: 1rem;
      height: 1rem;
      background: #fff;
      border-left: 1px solid var(--ev-line, #ececec);
      border-top: 1px solid var(--ev-line, #ececec);
      transform: rotate(45deg);
    }
  }
  /* items du sous-menu : liens DA propres (reset legacy #20005e + paddings + underline) */
  header#myHeader .menu .navigation-content a.landSubmenu {
    display: block;
    margin: 0;
    padding: 0;
  }
  header#myHeader .menu .navigation-content a.landSubmenu .dropdown-custom.btn {
    font-family: ${FONTS.body};
    font-weight: ${FONT_WEIGHT.semibold};
    font-style: normal;
    font-size: .92rem;
    color: #1a1430 !important;
    padding: .5rem .7rem !important;
    border-radius: .6rem;
    white-space: nowrap;
    transition: background .12s ease, color .12s ease;
  }
  header#myHeader .menu .navigation-content a.landSubmenu:hover .dropdown-custom.btn {
    background: rgba(194,24,166,.08);
    color: #C218A6 !important;
  }
  header#myHeader .menu .navigation-content .lines { display: none; }

  /* ============ Avatar (icône profil) ============ */
  header#myHeader .de-menu-profile > img {
    width: 2.6rem;
    height: 2.6rem;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #C218A6;
    box-shadow: 0 0 0 0.2rem rgba(194,24,166,.08);
    cursor: pointer;
  }

  /* ============ Cloche notifications ============ */
  header#myHeader .de-menu-notification {
    position: relative;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    border: 1px solid var(--ev-line, #ececec);
    background: #fff;
    display: grid;
    place-items: center;
    color: #0d0a10;
    margin-right: .9rem;
  }
  header#myHeader .de-menu-notification .d-count {
    position: absolute;
    top: -.3rem;
    right: -.3rem;
    min-width: 1.15rem;
    height: 1.15rem;
    border-radius: 1rem;
    background: #C218A6;
    color: #fff;
    font-family: ${FONTS.body};
    font-weight: ${FONT_WEIGHT.extrabold};
    font-size: .62rem;
    display: grid;
    place-items: center;
    padding: 0 .3rem;
  }

  /* ============ Panneau dropdown profil ============ */
  /* §96 polish : scale 0.9 sur tout le dropdown — réduit visuellement de 10%
     sans toucher chaque rule. transform-origin top right pour garder l'ancrage
     sous l'avatar (coin haut-droit du dropdown aligné avec coin bas-droit avatar). */
  header#myHeader .de-menu-profile .popshow {
    position: absolute;
    top: calc(100% + .8rem);
    right: 0;
    width: 18rem;
    background: #fff;
    border: 1px solid var(--ev-line, #ececec);
    border-top: .22rem solid #C218A6;
    border-radius: 1rem;
    box-shadow: 0 1.2rem 3rem rgba(33,12,46,.18);
    overflow: hidden;
    z-index: 200;
    padding: 0;
    transform: scale(0.9);
    transform-origin: top right;
  }
  header#myHeader .de-menu-profile .popshow::before {
    content: "";
    position: absolute;
    top: -.5rem;
    right: 1.1rem;
    width: 1rem;
    height: 1rem;
    background: #fff;
    border-left: 1px solid var(--ev-line, #ececec);
    border-top: 1px solid var(--ev-line, #ececec);
    transform: rotate(45deg);
  }

  /* ---- Entête (dd-head) : nom + rôle + handle + adresse ---- */
  header#myHeader .de-menu-profile .popshow .dd-head {
    display: flex;
    align-items: center;
    gap: .8rem;
    padding: 1.1rem 1.1rem .9rem;
  }
  header#myHeader .de-menu-profile .popshow .dd-head img {
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #C218A6;
    flex-shrink: 0;
  }
  header#myHeader .de-menu-profile .popshow .dd-name {
    font-family: ${FONTS.body};
    font-weight: ${FONT_WEIGHT.extrabold};
    font-size: 1.02rem;
    color: #0d0a10;
    line-height: 1.1;
    display: flex;
    align-items: center;
    gap: .4rem;
    flex-wrap: wrap;
  }
  header#myHeader .de-menu-profile .popshow .dd-role {
    font-family: ${FONTS.body};
    font-weight: ${FONT_WEIGHT.bold};
    font-size: .56rem;
    letter-spacing: .06em;
    text-transform: uppercase;
    color: #C218A6;
    background: rgba(194,24,166,.14);
    border: 1px solid rgba(194,24,166,.55);
    border-radius: 1rem;
    padding: .15em .55em;
  }
  header#myHeader .de-menu-profile .popshow .dd-addr {
    font-family: 'Roboto Mono', monospace;
    font-size: .66rem;
    color: rgba(13,10,16,.55);
    margin-top: .3rem;
    display: flex;
    align-items: center;
    gap: .4rem;
  }
  header#myHeader .de-menu-profile .popshow .dd-addr .dd-addr-copy {
    display: inline-flex;
    align-items: center;
    gap: .3em;
    border: 1px solid rgba(194,24,166,.45);
    background: rgba(194,24,166,.08);
    color: #C218A6;
    border-radius: .5rem;
    padding: .15em .45em;
    font-family: ${FONTS.body};
    font-size: .62rem;
    font-weight: ${FONT_WEIGHT.bold};
    cursor: pointer;
    line-height: 1;
    transition: background .15s, border-color .15s, color .15s;
  }
  header#myHeader .de-menu-profile .popshow .dd-addr .dd-addr-copy:hover {
    background: rgba(194,24,166,.16);
    border-color: rgba(194,24,166,.75);
  }
  header#myHeader .de-menu-profile .popshow .dd-addr .dd-addr-copy.copied {
    color: #15883d;
    border-color: rgba(31,203,92,.55);
    background: rgba(31,203,92,.12);
  }
  header#myHeader .de-menu-profile .popshow .dd-addr .dd-addr-copy i.fa { font-size: .68rem; }
  header#myHeader .de-menu-profile .popshow .dd-addr .dd-addr-copied { font-size: .6rem; }

  /* ---- Solde earnicoins (devise in-game) ---- */
  header#myHeader .de-menu-profile .popshow .dd-earnicoins {
    display: flex;
    align-items: center;
    gap: .5rem;
    margin: .55rem .1rem .1rem;
    padding: .5rem .65rem;
    border-radius: .625rem;
    background: ${COLORS.goldSoft};
    border: 1px solid ${COLORS.goldLine};
  }
  /* Petit lien "Get earnicoins" → poussé tout à droite, magenta souligné (inversé en dark, cf. ThemeVars) */
  header#myHeader .de-menu-profile .popshow .dd-earnicoins .dd-earnicoins-get {
    margin-left: auto;
    flex-shrink: 0;
    font-family: ${FONTS.body};
    font-weight: ${FONT_WEIGHT.semibold};
    font-size: .72rem;
    line-height: 1;
    color: ${COLORS.accent};
    text-decoration: underline;
    text-underline-offset: .12rem;
    white-space: nowrap;
    cursor: pointer;
    transition: color .12s ease;
  }
  header#myHeader .de-menu-profile .popshow .dd-earnicoins .dd-earnicoins-get:hover {
    color: ${COLORS.accentHover};
  }

  /* ---- Liséré dégradé ---- */
  header#myHeader .de-menu-profile .popshow .d-line {
    height: .12rem;
    background: linear-gradient(90deg, #C218A6, transparent);
    margin: 0 0 .35rem;
    border: 0;
  }

  /* ---- Liste d'items (cible 'li span' = couvre <li><a><span> ET <li><span>) ---- */
  header#myHeader .de-menu-profile .popshow .de-submenu-profile {
    list-style: none;
    margin: 0;
    padding: .3rem .5rem .5rem;
  }
  /* reset legacy style.scss : li{padding:5px} + a wrapper bloc plein largeur */
  header#myHeader .de-menu-profile .popshow .de-submenu-profile li {
    margin: 0;
    padding: 0;
    list-style: none;
  }
  header#myHeader .de-menu-profile .popshow .de-submenu-profile li a {
    display: block;
    width: 100%;
    padding: 0;
    text-decoration: none;
  }
  /* la ligne d'item = le span (icône + label), flex centré compact */
  header#myHeader .de-menu-profile .popshow .de-submenu-profile li span {
    display: flex;
    align-items: center;
    gap: .75rem;
    width: 100%;
    margin: 0;
    padding: .5rem .6rem;
    border-radius: .6rem;
    cursor: pointer;
    color: #0d0a10;
    text-align: left;
    font-family: ${FONTS.body};
    font-weight: ${FONT_WEIGHT.semibold};
    font-size: .9rem;
    line-height: 1.1;
    transition: background .12s ease;
  }
  header#myHeader .de-menu-profile .popshow .de-submenu-profile li span:hover {
    background: rgba(194,24,166,.08);
  }
  /* icône : pastille carrée accent (reset legacy cercle gris + margin) */
  header#myHeader .de-menu-profile .popshow .de-submenu-profile li span i.fa {
    width: 2rem;
    height: 2rem;
    min-width: 2rem;
    margin: 0;
    padding: 0;
    border-radius: .5rem;
    flex-shrink: 0;
    display: grid;
    place-items: center;
    background: rgba(194,24,166,.08);
    color: #C218A6;
    font-size: .95rem;
  }
  /* Sign out = danger (!important pour battre le color:!important legacy) */
  header#myHeader .de-menu-profile .popshow .de-submenu-profile li.dd-signout span {
    color: #E0312E !important;
  }
  header#myHeader .de-menu-profile .popshow .de-submenu-profile li.dd-signout span:hover {
    background: rgba(224,49,46,.07);
  }
  header#myHeader .de-menu-profile .popshow .de-submenu-profile li.dd-signout span i.fa {
    background: rgba(224,49,46,.08);
    color: #E0312E;
  }

  /* ============ Widget notifications (cloche) — variante C "Feed compact" ============ */
  header#myHeader .de-menu-notification .popshow.notif-pop {
    position: absolute;
    top: calc(100% + .8rem);
    right: 0;
    width: 23rem;
    background: #fff;
    border: 1px solid var(--ev-line, #ececec);
    border-top: .22rem solid #C218A6;
    border-radius: 1rem;
    box-shadow: 0 1.2rem 3rem rgba(33,12,46,.18);
    overflow: hidden;
    z-index: 200;
    padding: 0;
    cursor: default;
    /* colonne : entête fixe + liste scrollable + footer "View all" fixe en bas */
    display: flex;
    flex-direction: column;
    max-height: min(32rem, calc(100vh - 5rem));
  }
  header#myHeader .de-menu-notification .popshow.notif-pop::before {
    content: "";
    position: absolute;
    top: -.5rem;
    right: 1.1rem;
    width: 1rem;
    height: 1rem;
    background: #fff;
    border-left: 1px solid var(--ev-line, #ececec);
    border-top: 1px solid var(--ev-line, #ececec);
    transform: rotate(45deg);
  }
  /* En-tête compact */
  header#myHeader .de-menu-notification .popshow.notif-pop .nhead {
    display: flex;
    align-items: center;
    gap: .4rem;
    padding: .85rem 1rem;
    border-bottom: 1px solid var(--ev-line, #ececec);
    flex-shrink: 0;
  }
  header#myHeader .de-menu-notification .popshow.notif-pop .nhead h3 {
    font-family: ${FONTS.body};
    font-weight: ${FONT_WEIGHT.bold};
    font-size: .78rem;
    letter-spacing: .08em;
    text-transform: uppercase;
    color: rgba(13,10,16,.55);
    margin: 0;
  }
  header#myHeader .de-menu-notification .popshow.notif-pop .nnew {
    font-family: ${FONTS.body};
    font-weight: ${FONT_WEIGHT.extrabold};
    font-size: .78rem;
    color: #C218A6;
  }
  header#myHeader .de-menu-notification .popshow.notif-pop .nspacer { flex: 1; }
  header#myHeader .de-menu-notification .popshow.notif-pop .nmarkall {
    font-family: ${FONTS.body};
    font-weight: ${FONT_WEIGHT.bold};
    font-size: .72rem;
    color: #C218A6;
    cursor: pointer;
  }
  header#myHeader .de-menu-notification .popshow.notif-pop .nmarkall:hover { color: #A6148E; }
  /* Liste */
  header#myHeader .de-menu-notification .popshow.notif-pop ul {
    list-style: none;
    margin: 0;
    padding: 0;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
  }
  header#myHeader .de-menu-notification .popshow.notif-pop li {
    display: flex;
    align-items: center;
    gap: .65rem;
    padding: .6rem 1rem;
    margin: 0;
    cursor: pointer;
    position: relative;
    transition: background .12s ease;
  }
  header#myHeader .de-menu-notification .popshow.notif-pop li + li {
    border-top: 1px solid #f4f1f6;
  }
  header#myHeader .de-menu-notification .popshow.notif-pop li:hover {
    background: rgba(194,24,166,.05);
  }
  header#myHeader .de-menu-notification .popshow.notif-pop li.unread {
    background: rgba(194,24,166,.045);
  }
  header#myHeader .de-menu-notification .popshow.notif-pop li.unread::after {
    content: "";
    position: absolute;
    right: .8rem;
    top: 50%;
    transform: translateY(-50%);
    width: .45rem;
    height: .45rem;
    border-radius: 50%;
    background: #C218A6;
  }
  /* Icône typée (emoji + gradient catégorie) */
  header#myHeader .de-menu-notification .popshow.notif-pop .nic {
    width: 1.9rem;
    height: 1.9rem;
    min-width: 1.9rem;
    border-radius: .5rem;
    display: grid;
    place-items: center;
    font-size: .95rem;
    flex-shrink: 0;
  }
  header#myHeader .de-menu-notification .popshow.notif-pop .ntxt {
    flex: 1;
    min-width: 0;
    padding-right: .8rem;
  }
  header#myHeader .de-menu-notification .popshow.notif-pop .ntxt p {
    font-family: 'Roboto', sans-serif;
    font-size: .82rem;
    line-height: 1.3;
    color: #0d0a10;
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  header#myHeader .de-menu-notification .popshow.notif-pop .ntxt time {
    font-family: 'Roboto Mono', monospace;
    font-size: .62rem;
    color: rgba(13,10,16,.55);
  }
  /* Footer "View all" */
  header#myHeader .de-menu-notification .popshow.notif-pop .nfoot {
    padding: .7rem;
    background: #fff;
    border-top: 1px solid var(--ev-line, #ececec);
    flex-shrink: 0;
  }
  header#myHeader .de-menu-notification .popshow.notif-pop .nfoot .notification-button {
    display: block;
    text-align: center;
    font-family: ${FONTS.body};
    font-weight: ${FONT_WEIGHT.extrabold};
    font-style: italic;
    text-transform: uppercase;
    letter-spacing: .04em;
    font-size: .8rem;
    color: #fff;
    background: #C218A6;
    border-radius: .625rem;
    padding: .65em;
    cursor: pointer;
  }

  /* ============ Icônes droite normalisées 2.4rem + séparateur + Connect (§header-redesign) ============
     Cibles .mainside pour ne pas interférer avec les icônes mobile ou les popups.
     Toutes les icônes (Lang, Cart, Support, Bell via .de-menu-notification) partagent
     la même taille/forme ; le gap est géré par le flex parent. */
  header#myHeader .mainside .h-icon-btn {
    width: 2.4rem;
    height: 2.4rem;
    border-radius: 50%;
    border: 1px solid rgba(13,10,16,.1);
    background: transparent;
    display: grid;
    place-items: center;
    color: #1a1430;
    cursor: pointer;
    position: relative;
    transition: background .15s, border-color .15s, color .15s;
    flex-shrink: 0;
    padding: 0;
  }
  header#myHeader .mainside .h-icon-btn:hover {
    background: rgba(194,24,166,.06);
    border-color: rgba(194,24,166,.28);
    color: #C218A6;
  }
  /* séparateur vertical entre groupe icônes et CTA/avatar */
  header#myHeader .mainside .h-sep {
    width: 1px;
    height: 1.4rem;
    background: rgba(13,10,16,.09);
    flex-shrink: 0;
  }

  /* ============ Dark mode toggle (validé 2026-06-19) — OFF transparent / ON violet DA ============
     NB : ce fichier est l'exception "littéraux DA" documentée en tête ; les alpha-ink sans token
     portent ui-doctor-allow. La session dark mode tokenisera l'ensemble (cf. TRACK darkmode-menu). */
  header#myHeader .mainside .theme-toggle {
    position: relative;
    width: 3.7rem;
    height: 2rem;
    border-radius: 1rem;
    padding: 0;
    cursor: pointer;
    flex-shrink: 0;
    border: 1px solid rgba(13,10,16,.15); /* ui-doctor-allow : alpha ink, pas de token */
    background: transparent;                 /* OFF (light) = transparent */
    transition: background .3s ease, border-color .3s ease;
  }
  header#myHeader .mainside .theme-toggle.is-dark {
    background: #C218A6;            /* ON (dark) = violet DA — ui-doctor-allow : interpolation morte dans ce createGlobalStyle, littéral obligatoire (cf. en-tête fichier) */
    border-color: transparent;
  }
  /* glyphes soleil / lune */
  header#myHeader .mainside .theme-toggle .tt-glyph {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    font-size: .82rem;
    line-height: 1;
    pointer-events: none;
    transition: opacity .3s ease, color .3s ease;
  }
  header#myHeader .mainside .theme-toggle .tt-sun  { left: .5rem;  color: #C218A6; opacity: 1; } /* ui-doctor-allow : littéral obligatoire (interpolation morte ici) */
  header#myHeader .mainside .theme-toggle .tt-moon { right: .5rem; color: rgba(13,10,16,.3); opacity: .55; } /* ui-doctor-allow : alpha ink */
  header#myHeader .mainside .theme-toggle.is-dark .tt-sun  { color: rgba(255,255,255,.55); opacity: .55; } /* ui-doctor-allow : alpha white */
  header#myHeader .mainside .theme-toggle.is-dark .tt-moon { color: #fff; opacity: 1; } /* ui-doctor-allow : littéral obligatoire */
  /* knob glissant */
  header#myHeader .mainside .theme-toggle .tt-knob {
    position: absolute;
    top: 50%;
    left: .18rem;
    width: 1.55rem;
    height: 1.55rem;
    border-radius: 50%;
    transform: translateY(-50%);
    background: #fff; /* ui-doctor-allow : littéral obligatoire (interpolation morte dans ce createGlobalStyle) */
    box-shadow: 0 1px 0.2rem rgba(13,10,16,.25); /* ui-doctor-allow : alpha ink shadow */
    transition: left .3s cubic-bezier(.4,1.3,.5,1);
    z-index: 2;
  }
  header#myHeader .mainside .theme-toggle.is-dark .tt-knob { left: 1.97rem; }
  /* ---- Badge "mauvais réseau" — à gauche du toggle, statique (plus de clignotement) ----
     Affiché uniquement si wallet connecté + mauvaise chaîne (garde dans header.jsx). */
  header#myHeader .mainside .wrong-network-chip {
    display: inline-flex;
    align-items: center;
    gap: .4rem;
    height: 2rem;
    padding: 0 .7rem;
    border-radius: 1rem;
    font-family: 'Roboto', sans-serif;
    font-size: .74rem;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
    cursor: pointer;
    flex-shrink: 0;
    color: #d92d20;                        /* ui-doctor-allow : rouge alerte, littéral (createGlobalStyle) */
    background: rgba(217,45,32,.08);       /* ui-doctor-allow : alpha rouge */
    border: 1px solid rgba(217,45,32,.35); /* ui-doctor-allow : alpha rouge */
    transition: background .15s ease, border-color .15s ease;
  }
  header#myHeader .mainside .wrong-network-chip:hover {
    background: rgba(217,45,32,.14);       /* ui-doctor-allow : alpha rouge */
    border-color: rgba(217,45,32,.55);     /* ui-doctor-allow : alpha rouge */
  }
  header#myHeader .mainside .wrong-network-chip .wn-dot {
    width: .5rem;
    height: .5rem;
    border-radius: 50%;
    background: #d92d20;                    /* ui-doctor-allow : rouge alerte */
    flex-shrink: 0;
  }
  /* ---- Connect Wallet button — Roboto 700 non-italic, fond magenta ---- */
  header#myHeader .connect-wal .header-button {
    display: inline-flex !important;
    align-items: center !important;
    gap: 0.5rem !important;
    background: #C218A6 !important;
    color: #fff !important;
    border-radius: 0.625rem !important;
    padding: 0.5rem 1.1rem 0.5rem 0.8rem !important;
    font-family: ${FONTS.body} !important;
    font-weight: 700 !important;
    font-style: normal !important;
    font-size: 0.875rem !important;
    text-decoration: none !important;
    cursor: pointer !important;
    transition: background .15s, box-shadow .15s !important;
    letter-spacing: 0.01em !important;
    line-height: 1.2 !important;
  }
  header#myHeader .connect-wal .header-button:hover {
    background: #a0169a !important;
    box-shadow: 0 4px 14px rgba(194,24,166,.28) !important;
  }
  /* Pastille logo mini dans le bouton */
  header#myHeader .connect-wal .header-button .btn-logo {
    width: 1.2rem;
    height: 1.2rem;
    background: rgba(255,255,255,.2);
    border-radius: 50%;
    display: grid;
    place-items: center;
    flex-shrink: 0;
  }
  /* gap mainside pour aligner h-sep + les deux groupes enfants */
  @media (min-width: 992px) {
    header#myHeader .row.w-100-nav > .mainside { gap: 0.45rem; }
  }

  /* ======================================================================
     MOBILE HEADER — prise de contrôle totale à ≤ 991px (breakpoint lg Bootstrap)
     Layout : [icône EV (noir)] [spacer] [wallet-icon-btn (violet)] [☰]
     Menu : dropdown pleine largeur sous le header, masqué par défaut.
     Spécificité header#myHeader > .navbar → ces règles gagnent toujours.
     ====================================================================== */
  @media (max-width: 991px) {

    /* 1 ── Conteneur header : pas de débordement latéral */
    header#myHeader {
      overflow-x: hidden !important;
      position: relative !important;
    }

    /* 2 ── Row principale : flex [logo | spacer | mainside | burger] */
    header#myHeader .row.w-100-nav {
      display: flex !important;
      align-items: center !important;
      flex-wrap: nowrap !important;
      padding: 0.55rem 1rem !important;
      width: 100% !important;
      box-sizing: border-box !important;
      position: relative !important;
    }

    /* 3 ── Logo : icône EV uniquement, logos horizontal cachés */
    header#myHeader .logo {
      flex: 0 0 auto !important;
      position: static !important;
    }
    /* Masquer les logos horizontaux sur mobile */
    header#myHeader .logo .img-fluid:not(.logo-mobile-icon) {
      display: none !important;
    }
    /* Icône EV seule — noire (texte noir = filter brightness(0)) */
    header#myHeader .logo .logo-mobile-icon {
      display: block !important;
      height: 2rem !important;
      width: auto !important;
      filter: brightness(0) !important;
    }

    /* 4 ── Menu nav : masqué par défaut, dropdown quand .menu-open */
    header#myHeader .menu {
      display: none !important;
      position: static !important;
      flex: none !important;
    }
    header#myHeader .menu.menu-open {
      display: block !important;
      position: absolute !important;
      top: 100% !important;
      left: 0 !important;
      right: 0 !important;
      width: 100% !important;
      box-sizing: border-box !important;
      background: #fff !important;
      box-shadow: 0 0.75rem 2rem rgba(13, 10, 16, .18) !important;
      border-top: 0.2rem solid #C218A6 !important;
      z-index: 1001 !important;
      padding: 0.4rem 1.2rem 1rem !important;
    }
    header#myHeader .menu.menu-open .navbar-item {
      display: block !important;
      padding: 0.75rem 0 !important;
      border-bottom: 1px solid rgba(13, 10, 16, .07) !important;
    }
    header#myHeader .menu.menu-open .navbar-item:last-child {
      border-bottom: none !important;
    }
    /* Tous les liens du menu ouvert : sombres (override .navbar.white a{color:white}) */
    header#myHeader .menu.menu-open a,
    header#myHeader .menu.menu-open a:visited,
    header#myHeader .menu.menu-open a:hover,
    header#myHeader .menu.menu-open a.active,
    header#myHeader .menu.menu-open a.non-active {
      color: #1a1430 !important;
    }
    header#myHeader .menu.menu-open .dropdown-custom.btn {
      color: #1a1430 !important;
      font-family: ${FONTS.body};
      font-weight: ${FONT_WEIGHT.bold};
      font-size: ${FONT_SIZE.base};
      width: 100%;
      text-align: left;
      padding: 0;
    }
    /* Cacher les décorations desktop (tirets/flèches) dans le menu mobile */
    header#myHeader .menu.menu-open .lines {
      display: none !important;
    }
    header#myHeader .menu.menu-open .navigation-content {
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
      position: static !important;
      box-shadow: none !important;
      border: none !important;
      background: transparent !important;
      padding: 0.3rem 0 0.2rem 1rem;
    }
    header#myHeader .menu.menu-open .navigation-content a {
      color: #555 !important;
      font-size: 0.9rem;
      display: block;
      padding: 0.3rem 0;
    }
    header#myHeader .menu.menu-open .navigation-content a:hover {
      color: #C218A6 !important;
    }

    /* 5 ── Mainside (notif + avatar + connect + "Add 3D Asset") : RETIRÉE de la barre mobile.
       Ces accès vivent désormais dans le drawer (burger). Barre mobile = [logo | wallet centré | ☰].
       (§décision 5 : barre épurée, wallet au centre.) */
    header#myHeader .mainside { display: none !important; }

    /* 6 ── Bouton wallet MOBILE : centré absolu dans la barre, cercle violet DA.
       Rendu loggué OU délogué (ouvre EarniWalletModal : connect / manage). */
    header#myHeader .de-menu-wallet-mobile {
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      position: absolute !important;
      left: 50% !important;
      top: 50% !important;
      transform: translate(-50%, -50%) !important;
      width: 2.4rem !important;
      height: 2.4rem !important;
      padding: 0 !important;
      border-radius: 50% !important;
      background: rgba(194, 24, 166, 0.08) !important;
      border: 1.5px solid #C218A6 !important;
      cursor: pointer !important;
      z-index: 2 !important;
      transition: background 0.15s ease !important;
    }
    header#myHeader .de-menu-wallet-mobile:active {
      background: rgba(194, 24, 166, 0.22) !important;
    }
    /* icône wallet : SVG INLINE (rendu en JSX) — pas de data-URL CSS (cassait la minif). */
    header#myHeader .de-menu-wallet-mobile .wallet-ico {
      display: block !important;
      width: 1.25rem !important;
      height: 1.25rem !important;
    }

    /* 7 ── Hamburger : poussé tout à droite, barres sombres */
    header#myHeader button.nav-icon {
      position: static !important;
      flex: 0 0 auto !important;
      margin-left: auto !important;
      display: flex !important;
      flex-direction: column !important;
      justify-content: space-between !important;
      width: 1.5rem !important;
      height: 1rem !important;
      padding: 0 !important;
      background: none !important;
      border: none !important;
      cursor: pointer !important;
      z-index: 2 !important;
    }
    header#myHeader button.nav-icon .menu-line,
    header#myHeader button.nav-icon .menu-line1,
    header#myHeader button.nav-icon .menu-line2 {
      position: static !important;
      display: block !important;
      width: 100% !important;
      height: 2px !important;
      background: #1a1430 !important;
      border-radius: 2px !important;
      top: auto !important;
    }
  }

  /* Desktop ≥992px : cacher l'icône EV mobile + cacher le hamburger + cacher bouton wallet mobile */
  @media (min-width: 992px) {
    header#myHeader .logo .logo-mobile-icon { display: none !important; }
    header#myHeader button.nav-icon { display: none !important; }
    header#myHeader .de-menu-wallet-mobile { display: none !important; }
  }

  /* ── Offcanvas mobile nav (earni-mobile-nav) ─────────────────────────── */
  .offcanvas.earni-mobile-nav {
    background: #0f0418 !important;
    color: #fff !important;
    width: 80vw !important;
    max-width: 20rem !important;
    border-right: 1px solid rgba(194, 24, 166, 0.3) !important;
  }
  .offcanvas.earni-mobile-nav .offcanvas-header {
    background: linear-gradient(135deg, #1a0536 0%, #2a0850 100%) !important;
    border-bottom: 1px solid rgba(194, 24, 166, 0.25) !important;
    padding: 1rem 1.25rem !important;
  }
  .offcanvas.earni-mobile-nav .btn-close {
    filter: invert(1) !important;
    opacity: 0.7 !important;
  }
  .offcanvas.earni-mobile-nav .offcanvas-body {
    padding: 0 !important;
    overflow-y: auto !important;
  }
  .emn-nav {
    padding: 0.5rem 0 !important;
  }
  .emn-link {
    display: flex !important;
    align-items: center !important;
    gap: 0.625rem !important;
    padding: 0.75rem 1.25rem !important;
    color: #e0d8f0 !important;
    text-decoration: none !important;
    font-size: 0.9375rem !important;
    font-family: ${FONTS.body} !important;
    font-weight: ${FONT_WEIGHT.medium} !important;
    transition: background 0.15s ease, color 0.15s ease !important;
    border-left: 3px solid transparent !important;
  }
  .emn-link:hover, .emn-link.active {
    background: rgba(194, 24, 166, 0.12) !important;
    color: #C218A6 !important;
    border-left-color: #C218A6 !important;
    text-decoration: none !important;
  }
  .emn-link.emn-sub {
    padding-left: 2.25rem !important;
    font-size: ${FONT_SIZE.sm} !important;
    font-weight: ${FONT_WEIGHT.normal} !important;
    color: #b8a8d0 !important;
  }
  /* badge compteur notifications (drawer mobile) */
  .emn-link .emn-notif-count {
    margin-left: auto !important;
    min-width: 1.15rem !important;
    height: 1.15rem !important;
    padding: 0 0.3rem !important;
    border-radius: 1rem !important;
    background: #C218A6 !important;
    color: #fff !important;
    font-family: ${FONTS.body} !important;
    font-weight: ${FONT_WEIGHT.extrabold} !important;
    font-size: ${FONT_SIZE.xs} !important;
    display: inline-grid !important;
    place-items: center !important;
  }
  .emn-section-label {
    padding: 0.5rem 1.25rem 0.25rem !important;
    font-size: 0.7rem !important;
    font-weight: ${FONT_WEIGHT.bold} !important;
    text-transform: uppercase !important;
    letter-spacing: 0.08em !important;
    color: #C218A6 !important;
    font-family: ${FONTS.body} !important;
  }
  .emn-divider {
    height: 1px !important;
    background: rgba(194, 24, 166, 0.2) !important;
    margin: 0.5rem 1.25rem !important;
  }
  .emn-profile {
    padding: 0.75rem 0 !important;
  }
  .emn-profile-head {
    display: flex !important;
    align-items: center !important;
    gap: 0.75rem !important;
    padding: 0.5rem 1.25rem 0.75rem !important;
  }
  .emn-avatar {
    width: 2.5rem !important;
    height: 2.5rem !important;
    border-radius: 50% !important;
    object-fit: cover !important;
    flex-shrink: 0 !important;
    border: 2px solid rgba(194, 24, 166, 0.4) !important;
  }
  .emn-name {
    font-size: ${FONT_SIZE.sm} !important;
    font-weight: ${FONT_WEIGHT.semibold} !important;
    color: #fff !important;
    display: flex !important;
    align-items: center !important;
    gap: 0.4rem !important;
    flex-wrap: wrap !important;
  }
  .emn-badge {
    font-size: ${FONT_SIZE.xs} !important;
    background: #C218A6 !important;
    color: #fff !important;
    padding: 0.1rem 0.4rem !important;
    border-radius: 0.3rem !important;
    text-transform: uppercase !important;
    font-weight: ${FONT_WEIGHT.bold} !important;
  }
  .emn-addr {
    font-size: ${FONT_SIZE.xs} !important;
    color: #9080b0 !important;
    font-family: 'Roboto Mono', monospace !important;
    margin-top: 0.125rem !important;
  }
  .emn-wallet-btn {
    display: flex !important;
    align-items: center !important;
    gap: 0.625rem !important;
    width: 100% !important;
    padding: 0.75rem 1.25rem !important;
    background: rgba(194, 24, 166, 0.15) !important;
    color: #C218A6 !important;
    border: none !important;
    font-size: 0.9375rem !important;
    font-family: ${FONTS.body} !important;
    font-weight: ${FONT_WEIGHT.semibold} !important;
    cursor: pointer !important;
    transition: background 0.15s ease !important;
    text-align: left !important;
  }
  .emn-wallet-btn:hover {
    background: rgba(194, 24, 166, 0.25) !important;
  }
  .emn-signout {
    display: flex !important;
    align-items: center !important;
    gap: 0.625rem !important;
    width: 100% !important;
    padding: 0.75rem 1.25rem !important;
    background: transparent !important;
    color: #ff6b6b !important;
    border: none !important;
    font-size: 0.9375rem !important;
    font-family: ${FONTS.body} !important;
    font-weight: ${FONT_WEIGHT.medium} !important;
    cursor: pointer !important;
    transition: background 0.15s ease !important;
    text-align: left !important;
  }
  .emn-signout:hover {
    background: rgba(255, 107, 107, 0.1) !important;
  }
  .emn-connect {
    padding: 1.25rem !important;
  }
  .emn-connect-btn {
    display: block !important;
    width: 100% !important;
    padding: 0.75rem 1rem !important;
    background: #C218A6 !important;
    color: #fff !important;
    border: none !important;
    border-radius: 0.625rem !important;
    font-size: 0.9375rem !important;
    font-family: ${FONTS.body} !important;
    font-weight: ${FONT_WEIGHT.semibold} !important;
    cursor: pointer !important;
    transition: background 0.15s ease !important;
    text-align: center !important;
  }
  .emn-connect-btn:hover {
    background: #a0169a !important;
  }
`;
